import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import React from "react";
import { createRoot } from "react-dom/client";

import { Toaster } from "@/components/ui/sonner";
import "@/i18n";
import { OrganizationProvider } from "@/lib/organization";
import { getToken } from "@/lib/pocketbase";
import { PocketProvider } from "@/lib/pocketbase";

import { routeTree } from "./routeTree.gen";

import "./css/main.css";

let sentry_handlers = {};

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://3f529cdcae50f6cedadd4f9671369eb5@o451249.ingest.us.sentry.io/4507338684497920",
    integrations: [],
  });
  // React 19 error handlers
  sentry_handlers = {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an Error Boundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ meta }) => {
        const token = getToken();
        const combined_url = `/api${meta?.url ?? ""}`;
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        const response = await fetch(combined_url, { headers });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      },
      meta: {
        url: "",
      },
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { url?: string };
  }
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface RouterContext {
    queryClient: QueryClient;
  }
}

createRoot(document.getElementById("root") as HTMLDivElement, {
  ...sentry_handlers,
}).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <PocketProvider>
        <OrganizationProvider>
          <RouterProvider router={router} />
          <Toaster />
        </OrganizationProvider>
      </PocketProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
